import PageHero from "@/components/PageHero";
import { BookOpen, GraduationCap, HandHeart, Library, Calendar, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const resources = [
  {
    icon: BookOpen,
    title: "Homeowner Guides",
    body: "Plain-language guides to understanding hardship options, equity, foreclosure timelines, and your rights as a Hawaii homeowner.",
    status: "Coming Soon",
  },
  {
    icon: GraduationCap,
    title: "Financial Literacy",
    body: "Workshops and educational materials covering budgeting, refinancing basics, and long-term housing planning.",
    status: "Coming Soon",
  },
  {
    icon: HandHeart,
    title: "Assistance Partners",
    body: "A growing directory of trusted local nonprofits, agencies, and community programs that can help.",
    status: "In Development",
  },
  {
    icon: Library,
    title: "Knowledge Library",
    body: "Articles and explainers covering Hawaii-specific housing topics: kuleana, leasehold, ohana zoning and more.",
    status: "Coming Soon",
  },
  {
    icon: Calendar,
    title: "Community Workshops",
    body: "In-person and virtual sessions where homeowners can learn, ask questions, and connect with neighbors.",
    status: "Planned",
  },
  {
    icon: MessageSquare,
    title: "Talk-Story Sessions",
    body: "Listening sessions to hear directly from local families about the housing challenges they face.",
    status: "Planned",
  },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <PageHero
        testIdPrefix="resources-hero"
        eyebrow="Resources"
        title="Housing assistance & education."
        subtitle="As we grow, we're building a library of resources and partnerships to help Hawaii families achieve greater housing stability."
      />

      <section className="bg-brand-ivory py-24 md:py-32 relative overflow-hidden" data-testid="resources-grid-section">
        <div className="absolute inset-0 bg-topo opacity-100 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="eyebrow text-brand-gold">What's Ahead</p>
            <h2 className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight">
              Resources we're building for our community.
            </h2>
            <p className="mt-6 text-brand-ink/70 text-base md:text-lg leading-relaxed font-light">
              Have a topic you'd like us to cover? A program we should know about? We'd love to hear
              from you — your input shapes what comes next.
            </p>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((r, i) => {
              const Icon = r.icon;
              return (
                <article
                  key={r.title}
                  data-testid={`resource-card-${i}`}
                  className="group bg-white border border-brand-forest/10 p-8 rounded-sm hover:border-brand-gold transition-all duration-500 flex flex-col"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-forest/5 rounded-sm group-hover:bg-brand-forest transition-colors duration-500">
                      <Icon size={20} className="text-brand-forest group-hover:text-brand-gold transition-colors" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-sm">
                      {r.status}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl text-brand-ink leading-snug">{r.title}</h3>
                  <p className="mt-4 text-brand-ink/70 text-sm leading-relaxed font-light flex-1">
                    {r.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-forest text-white py-24 md:py-28" data-testid="resources-cta">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight">
            Want to be the first to know? <br />
            <span className="text-brand-gold italic">Let's stay connected.</span>
          </h2>
          <p className="mt-6 text-white/80 max-w-2xl mx-auto text-lg font-light">
            Reach out and we'll keep you updated as resources, workshops, and partnerships launch.
          </p>
          <Link
            to="/contact"
            data-testid="resources-cta-btn"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-brand-gold text-brand-ink text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-white transition-all duration-300"
          >
            Get in Touch <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
